import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import { selectUser } from '@/redux/user/selectors';
import { Context, ConversationSource } from '@/shared/types/chat';
import { AmendPayload, Correction, LanguageModelOverride } from '@/shared/types/tryIt';

import { ConversationTurn } from '../types/conversation';
import { TabId, TryItState } from '../types/state';
import { fillTerminalAuth } from '../utils/authTerminalState';

export const defaultState: TryItState = {
  mode: null,
  messages: [],
  selection: null,
  context: [],
  lastRequestId: null,
  ui: { activeTab: TabId.Details },
  source: null,
  isAmended: false,
  languageModelOverride: null,
};

type OpenTestPayload = { tryItSessionId: string; brainId: string };
type OpenReplayPayload = { sessionId: string; sessionStart?: string; language?: string };
type SendSucceededPayload = {
  turns: ConversationTurn[];
  requestId: string | null;
  context: Context[];
};
type ReplayContentLoadedPayload = { turns: ConversationTurn[]; brainId?: string };
type ReplayLanguageChangedPayload = { language: string | undefined };
type TurnAmendedPayload = { requestId: string; correction: Correction };

// Each reducer below first checks `state.mode?.kind` and returns early if it
// doesn't match — e.g. `sendStarted` only runs in test mode, `replayFailed`
// only in replay mode. This lets selectors return `null` for the inactive
// mode without the consumer needing extra guards.
const slice = createSlice({
  name: 'tryIt',
  initialState: defaultState,
  reducers: {
    openTest: (_state, action: PayloadAction<OpenTestPayload>) => ({
      ...defaultState,
      mode: {
        kind: 'test',
        tryItSessionId: action.payload.tryItSessionId,
        brainId: action.payload.brainId,
        hasMessages: false,
        phase: 'idle',
      },
    }),
    openReplay: (_state, action: PayloadAction<OpenReplayPayload>) => ({
      ...defaultState,
      mode: {
        kind: 'replay',
        sessionId: action.payload.sessionId,
        sessionStart: action.payload.sessionStart,
        language: action.payload.language,
        phase: 'loading',
      },
    }),
    // The simulation preview snapshots the slice on mount and restores it here.
    restoreState: (_state, action: PayloadAction<TryItState>) => action.payload,

    sendStarted: (state) => {
      if (state.mode?.kind === 'test') {
        state.mode.phase = 'sending';
      }
    },
    // Optimistic user turn pushed the instant a message is sent, so the chat
    // shows it immediately instead of waiting for the brain response.
    appendPendingTurn: (state, action: PayloadAction<ConversationTurn>) => {
      if (state.mode?.kind !== 'test') {
        return;
      }
      state.messages.push(action.payload);
      state.mode.hasMessages = true;
    },
    sendSucceeded: (state, action: PayloadAction<SendSucceededPayload>) => {
      if (state.mode?.kind !== 'test') {
        return;
      }
      // Agent returned no response — keep the user's turn (finalized) instead of erasing it.
      if (action.payload.turns.length === 0) {
        state.messages.forEach((turn) => {
          if (turn.pending) {
            turn.pending = false;
            turn.id = action.payload.requestId ?? turn.id;
            turn.requestId = action.payload.requestId ?? turn.requestId;
          }
        });
        state.lastRequestId = action.payload.requestId;
        state.mode.phase = 'idle';
        return;
      }
      // Drop the optimistic pending turn(s) before appending the real response.
      while (state.messages.at(-1)?.pending) {
        state.messages.pop();
      }
      state.messages.push(...action.payload.turns);
      fillTerminalAuth(state.messages);
      state.context = action.payload.context;
      state.lastRequestId = action.payload.requestId;
      state.mode.phase = 'idle';
      state.mode.hasMessages = state.messages.length > 0;
    },
    sendFailed: (state) => {
      if (state.mode?.kind !== 'test') {
        return;
      }
      state.messages.forEach((turn, index) => {
        if (turn.pending) {
          turn.pending = false;
          turn.id = `error-${index}`;
          turn.metadata.isError = true;
        }
      });
      state.mode.phase = 'idle';
    },
    resetTest: (state, action: PayloadAction<{ tryItSessionId: string }>) => {
      if (state.mode?.kind !== 'test') {
        return;
      }
      state.messages = [];
      state.selection = null;
      state.context = [];
      state.lastRequestId = null;
      // Back to Details so a fresh conversation opens on the empty state.
      state.ui.activeTab = TabId.Details;
      state.mode.tryItSessionId = action.payload.tryItSessionId;
      state.mode.hasMessages = false;
      state.mode.phase = 'idle';
    },
    // Replace the per-turn context stack. The test-mode Context-variables
    // editor edits the last (current) entry and dispatches the new array; the
    // next test send reads `context[last]` as its outgoing context.
    updateContext: (state, action: PayloadAction<Context[]>) => {
      state.context = action.payload;
    },

    replayContentLoaded: (state, action: PayloadAction<ReplayContentLoadedPayload>) => {
      if (state.mode?.kind !== 'replay') {
        return;
      }
      state.messages = action.payload.turns;
      if (action.payload.brainId) {
        state.mode.brainId = action.payload.brainId;
      }
      state.mode.phase = 'ready';
    },

    replayTranslating: (state) => {
      if (state.mode?.kind !== 'replay' || state.mode.phase === 'loading') {
        return;
      }
      state.mode.phase = 'translating';
    },
    replayTranslated: (state, action: PayloadAction<ReplayContentLoadedPayload>) => {
      if (state.mode?.kind !== 'replay' || state.mode.phase === 'loading') {
        return;
      }
      state.messages = action.payload.turns;
      state.mode.phase = 'ready';
    },
    replayFailed: (state) => {
      if (state.mode?.kind === 'replay') {
        state.mode.phase = 'error';
      }
    },
    replayLanguageChanged: (state, action: PayloadAction<ReplayLanguageChangedPayload>) => {
      if (state.mode?.kind !== 'replay') {
        return;
      }
      // No-op if the language hasn't actually changed. Without this, a mirror
      // effect (e.g. `TranslateDropdown`'s `useTranslationState` → redux sync)
      // that fires `replayLanguageChanged(undefined)` on mount would clobber
      // the phase set by `openReplay`.
      if (state.mode.language === action.payload.language) {
        return;
      }
      state.mode.language = action.payload.language;
      // Preserve `loading` — the initial content fetch hasn't completed yet,
      // and only `replayContentLoaded` / `replayFailed` are allowed to exit
      // the loading phase. Once we're in `ready` / `translating` / `error`,
      // a language change can transition to `translating` / `ready` normally.
      if (state.mode.phase === 'loading') {
        return;
      }
      state.mode.phase = action.payload.language ? 'translating' : 'ready';
    },

    // Selection identifies a turn by its position in `messages` — the only
    // stable, unique key. `node_id` / `request_id` collide when the same node
    // is triggered twice (and are absent on campaign/agent turns), which made
    // duplicate turns highlight together and non-brain turns unselectable.
    selectTurn: (state, action: PayloadAction<number>) => {
      state.selection = {
        turnIndex: action.payload,
        actionId: null,
        requisiteIdx: null,
        ifNoRequisite: null,
      };
    },
    setActiveTab: (state, action: PayloadAction<TabId>) => {
      state.ui.activeTab = action.payload;
    },

    setSessionSource: (state, action: PayloadAction<ConversationSource | null>) => {
      state.source = action.payload;
    },

    setLanguageModelOverride: (state, action: PayloadAction<LanguageModelOverride | null>) => {
      state.languageModelOverride = action.payload;
    },

    clearLanguageModelOverride: (state) => {
      state.languageModelOverride = null;
    },

    turnAmended: (state, action: PayloadAction<TurnAmendedPayload>) => {
      state.isAmended = true;
      state.messages.forEach((turn) => {
        const requestId = turn.requestId ?? turn.metadata.nodesStack?.[0]?.request_id;
        if (requestId === action.payload.requestId) {
          turn.corrections = [...(turn.corrections ?? []), action.payload.correction];
        }
      });
    },
  },
});

export const {
  setSessionSource,
  setLanguageModelOverride,
  clearLanguageModelOverride,

  openTest,
  openReplay,
  restoreState,
  sendStarted,
  appendPendingTurn,
  sendSucceeded,
  sendFailed,
  resetTest,
  updateContext,
  replayContentLoaded,
  replayTranslating,
  replayTranslated,
  replayFailed,
  replayLanguageChanged,
  selectTurn,
  setActiveTab,
  turnAmended,
} = slice.actions;

/** Enter replay mode. */
export const enterReplay = (payload: OpenReplayPayload) => (dispatch) => {
  dispatch(openReplay(payload));
};

/** Enter test mode. */
export const enterTest = (payload: OpenTestPayload) => (dispatch) => {
  dispatch(openTest(payload));
};

/** Reset the active test conversation under a fresh session id. */
export const resetConversation = () => (dispatch) => {
  dispatch(resetTest({ tryItSessionId: uuidv4() }));
};

export const submitCorrection =
  (payload: AmendPayload & { correction: string }) => (dispatch, getState) => {
    dispatch(
      turnAmended({
        requestId: payload.request_id,
        correction: {
          task: payload.task,
          correction: payload.correction,
          language: payload.language,
          time: new Date().toISOString(),
          edited_by: selectUser(getState())?.email ?? '',
        },
      })
    );
  };

export default slice.reducer;
